import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowDown } from "lucide-react";
import { scrollToId } from "@/components/Navbar";

const HERO_BG =
  "https://images.unsplash.com/photo-1561197582-95ece0848664?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxkaXNjbyUyMGJhbGwlMjBuZW9ufGVufDB8fHx8MTc5MTQ3MTk5OHww&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden flex flex-col"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={HERO_BG}
          alt="Disco ball glowing under neon light"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-disco-void to-transparent" />

      <motion.div
        data-testid="hero-brand"
        initial={{ opacity: 0, y: 36, scale: 0.92 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 flex justify-center pt-24 sm:pt-28 pointer-events-none"
      >
        <div className="relative">
          <motion.img
            src="/images/mascot.png"
            alt="Disco Dogs hot dog mascot"
            data-testid="hero-mascot"
            animate={{ y: [0, -10, 0], rotate: [4, 8, 4] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-7 -right-4 w-16 sm:-top-16 sm:-right-14 sm:w-28 lg:w-32"
          />
          <img
            src="/images/wordmark.png"
            alt="Disco Dogs"
            data-testid="hero-wordmark"
            className="w-56 sm:w-[340px] lg:w-[400px] drop-shadow-[0_10px_40px_rgba(0,0,0,0.7)]"
            draggable={false}
          />
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mt-auto w-full max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-24 sm:pb-28"
      >
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="text-disco-teal text-xs sm:text-sm font-semibold tracking-[0.35em] uppercase mb-5"
        >
          Disco Dogs - Rolling Hot Dog Cart
        </motion.p>

        <h1 className="font-display uppercase leading-[0.86] tracking-tight text-[19vw] sm:text-[13vw] lg:text-[9.5rem]">
          <MaskedLine delay={0.45}>
            <span className="text-[#F8F8F8]">Street Dogs.</span>
          </MaskedLine>
          <MaskedLine delay={0.6}>
            <span className="neon-yellow">Disco</span>{" "}
            <span className="neon-teal">Lights.</span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subcopy"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-6 max-w-md text-sm sm:text-base text-zinc-300 font-light"
        >
          Gourmet hot dogs slung from a glowing cart. Find tonight's pitch, work
          through the photo menu, or book the whole disco for your event.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-see-menu-button"
            onClick={() => scrollToId("menu")}
            className="rounded-full bg-disco-yellow text-disco-void font-semibold px-7 py-3 text-sm transition-all duration-300 hover:shadow-[0_0_30px_rgba(226,255,0,0.45)] active:scale-95"
          >
            See the Menu
          </button>
          <button
            data-testid="hero-find-cart-button"
            onClick={() => scrollToId("find-us")}
            className="rounded-full border border-disco-teal/60 text-disco-teal font-semibold px-7 py-3 text-sm inline-flex items-center gap-2 transition-all duration-300 hover:bg-disco-teal/10 hover:border-disco-teal active:scale-95"
          >
            <MapPin size={16} />
            Find the Cart
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-zinc-500 animate-pulse-glow"
        aria-hidden="true"
      >
        <ArrowDown size={20} />
      </motion.div>
    </section>
  );
}
